import { type SubmitHandler, useForm } from 'react-hook-form';

import {
    REUSABLE_CONTENT_TITLE_MAX_LENGTH,
    type RevisionReusableContent,
} from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Form,
    Heading,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

interface RenameReusableContentDialogProps {
    reusableContent: RevisionReusableContent;

    /** Callback once the reusable content has been renamed, it can be used to track an event */
    onRenamed?: () => void;
}

/**
 * Dialog to rename a reusable content.
 */
export function RenameReusableContentDialog(props: RenameReusableContentDialogProps) {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Rename reusable content</Heading>
                <Description>
                    This is the name which will be used to reference your reusable content
                    throughout the space.
                </Description>
            </DialogHeader>
            <ReusableContentEditTitleForm {...props} />
        </Dialog>
    );
}

type FormInputs = {
    title: string;
};

function ReusableContentEditTitleForm(props: RenameReusableContentDialogProps) {
    const { reusableContent, onRenamed } = props;

    const branch = useHiveBranch();
    const [isPending, startDialogAction] = useDialogAction();
    const form = useForm<FormInputs>({
        defaultValues: { title: reusableContent.title },
    });
    const onSubmit: SubmitHandler<FormInputs> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                assertIsDefined(branch);

                await branch.change((branch) => {
                    branch.updateReusableContent(reusableContent.id, {
                        title: data.title,
                    });
                });

                onRenamed?.();

                await close();
            }, 'Error while updating title');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField name="title">
                    <TextInput
                        placeholder="Recognizable title for your team"
                        autoFocus
                        disabled={isPending}
                        maxLength={REUSABLE_CONTENT_TITLE_MAX_LENGTH}
                    />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Rename" />
            </DialogFooter>
        </Form>
    );
}
